import { useEffect, useRef } from "react"
import { useFormContext } from "@carrotfertility/carotene-core-x"
import type { UseFormSetValue } from "react-hook-form"
import { TOTAL_STEP_SLOTS, arrangeStepRows } from "./useInitialCarrotPlanSteps"
import type { CarrotPlanFormOption, MenuItemOptionFields } from "./useCarrotPlanMappings"
import { FieldName } from "../CarrotPlanFormTypes"
import type { CarrotPlanFormStep } from "../CarrotPlanFormTypes"

type UseResetFormStepsOnJourneyChangeArgs = {
  journey: number
  stage: number
  newRequiredItems: CarrotPlanFormOption<MenuItemOptionFields>[] | null
}

function applyStepToForm(setValue: UseFormSetValue<any>, step: CarrotPlanFormStep, index: number): void {
  const opts = { shouldDirty: true }
  setValue(`${FieldName.steps}.${index}.${FieldName.menuItem}`, step.menuItem ?? null, opts)
  setValue(`${FieldName.steps}.${index}.${FieldName.topic}`, step.topic ?? null, opts)
  setValue(`${FieldName.steps}.${index}.${FieldName.content}`, step.content ?? null, opts)
  setValue(`${FieldName.steps}.${index}.${FieldName.url}`, step.url ?? null, opts)
}

/**
 * Builds new required step rows, carrying over media (topic / content / url) from any
 * prior step sharing the same menuItem.value. Both `id` and `stepIndex` are temp placeholders.
 */
export function rebuildRequiredSteps(
  newRequiredItems: CarrotPlanFormOption<MenuItemOptionFields>[],
  previousSteps: CarrotPlanFormStep[]
): CarrotPlanFormStep[] {
  const previousByMenuValue = new Map<string, CarrotPlanFormStep>()
  for (const step of previousSteps) {
    const id = step.menuItem?.value
    if (id != null) previousByMenuValue.set(id, step)
  }

  return newRequiredItems.slice(0, TOTAL_STEP_SLOTS).map((item) => {
    const carryover = previousByMenuValue.get(item.value)
    return {
      id: "",
      stepIndex: 0,
      menuItem: item,
      topic: carryover?.topic,
      content: carryover?.content,
      url: carryover?.url ?? item.optionFields?.defaultUrl ?? null
    }
  })
}

/**
 * Returns previous user-added step rows that survive a swap: non-empty,
 * non-required, and not colliding with `newRequiredIds`.
 */
export function getUserAddedSteps(
  previousSteps: CarrotPlanFormStep[],
  newRequiredIds: Set<string>
): CarrotPlanFormStep[] {
  return previousSteps.filter((step) => {
    if (step.menuItem == null) return false
    if (step.menuItem.optionMetadata?.isRequired === true) return false
    if (newRequiredIds.has(step.menuItem.value)) return false
    return true
  })
}

const selectionKey = (journey: number, stage: number): string => `${journey}:${stage}`

/**
 * Re-seeds the form's step rows when the primary journey or stage changes,
 * preserving user-added steps and (where possible) media selections.
 *
 * Two branches that are mutually exclusive in a single run:
 *   1. Journey just changed → drop old required items immediately. The early
 *      `return` is load-bearing: react-query may have cached the
 *      (newJourney, staleStage) options, which would otherwise let branch 2
 *      fire with the wrong stage's required items.
 *   2. `newRequiredItems` is non-null (caller signals ready) + new selection → full swap.
 */
export function useResetFormStepsOnJourneyChange({
  journey,
  stage,
  newRequiredItems
}: UseResetFormStepsOnJourneyChangeArgs): void {
  const { setValue, getValues } = useFormContext()

  const lastJourneyRef = useRef(journey)
  const lastSwappedSelectionRef = useRef<string | null>(newRequiredItems != null ? selectionKey(journey, stage) : null)

  useEffect(() => {
    const swapRequiredItems = (requiredItems: CarrotPlanFormOption<MenuItemOptionFields>[]) => {
      const previousSteps = (getValues(FieldName.steps) ?? []) as CarrotPlanFormStep[]
      const newRequiredIds = new Set(requiredItems.map((m) => m.value))

      const requiredSteps = rebuildRequiredSteps(requiredItems, previousSteps)
      const userAddedSteps = getUserAddedSteps(previousSteps, newRequiredIds)

      const nextSteps = arrangeStepRows(userAddedSteps, requiredSteps)
      nextSteps.forEach((step, index) => applyStepToForm(setValue, step, index))
    }

    // Branch 1: journey just changed. Drop old required items now; we don't yet
    // know the new ones (the stage is about to reset to NONE).
    if (lastJourneyRef.current !== journey) {
      swapRequiredItems([])
      lastJourneyRef.current = journey
      // Invalidate so the next valid (journey, stage) the user picks re-triggers
      // a full swap, even if its selection happens to match the old one.
      lastSwappedSelectionRef.current = null
      return
    }

    // Branch 2: caller has handed us the required items for this selection and
    // we haven't already swapped for it.
    const currentSelection = selectionKey(journey, stage)
    const shouldSwap = newRequiredItems != null && lastSwappedSelectionRef.current !== currentSelection
    if (!shouldSwap) return

    swapRequiredItems(newRequiredItems)
    lastSwappedSelectionRef.current = currentSelection
  }, [newRequiredItems, journey, stage, setValue, getValues])
}
